import { navigateTo } from '@app/core/routing';
import { allApiModules, buildApiClient } from '@mi-front/core';
import { isAxiosError } from 'axios';
import { useEffect } from 'react';
import { config } from '../config';

/**
 * App-wide API client singleton. Prefer `useApiClient()` in React code so the
 * unauthentication handler is registered; import this only from non-React modules.
 */
export const apiClient = buildApiClient(
  {
    apiRoot: config.apiRoot,
  },
  allApiModules,
);

type AuthErrorResponse = {
  code?: string;
};

/**
 * Hard re-auth when the session is no longer usable:
 * - Token provider failed (refresh/access token expired or revoked)
 * - API returned 401 with invalid_token
 */
function handleUnauthentication(err: unknown) {
  if (isAxiosError(err)) {
    const responseData = (err.response?.data || {}) as AuthErrorResponse;

    if (responseData.code === 'invalid_token') {
      navigateTo('/logout');
    }
    return;
  }

  // Non-Axios failure from the request interceptor (getAccessTokenSilently rejected).
  navigateTo('/logout');
}

/**
 * Returns the shared apiClient and registers the unauthentication → logout handler.
 * Call once near the auth root (e.g. TokenGate).
 */
export function useApiClient() {
  useEffect(() => {
    apiClient.onUnauthentication(handleUnauthentication);

    return () => {};
  }, []);

  return apiClient;
}
